// ─── User ────────────────────────────────────────────────────────────

export interface User {
  userId: string;
  email: string;
  displayName?: string;
}

// ─── Sites ───────────────────────────────────────────────────────────

export type SiteStatus = 'draft' | 'building' | 'live' | 'error';

export interface Site {
  siteId: string;
  userId: string;
  siteName: string;
  templateId: string;
  templateVersion?: string;
  customDomain: string | null;
  s3Bucket: string | null;
  siteUrl: string | null;
  subdomain: string | null;
  lastDeployedAt: string | null;
  status: SiteStatus;
  allowedTemplates?: string[];
  createdAt: string;
  updatedAt: string;
  /** Build step timings in ms, keyed by step name (e.g. "template", "build", "s3Sync") */
  lastBuildTimings?: Record<string, number>;
  /** Total build duration in ms */
  lastBuildDurationMs?: number;
  /** Error message set when status is 'error' */
  errorMessage?: string;
  /** Preview build status: 'building' or 'ready' */
  previewStatus?: 'building' | 'ready' | 'error';
  /** Error message set when previewStatus is 'error' */
  previewErrorMessage?: string;
}

// ─── Content ─────────────────────────────────────────────────────────

export type ContentType = 'hero' | 'about' | 'product' | 'gallery' | 'contact' | 'site' | 'post' | 'category' | 'testimonial' | 'hours' | 'submission';

export interface ContentItem {
  siteId: string;
  contentKey: string;
  contentType: ContentType;
  data: Record<string, unknown>;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Extract the ID portion from a contentKey like "product#01HXYZ" */
export function getContentId(contentKey: string): string {
  const parts = contentKey.split('#');
  return parts.length > 1 ? parts[1] : contentKey;
}

// ─── Media ───────────────────────────────────────────────────────────

export interface MediaItem {
  key: string;
  mediaId: string;
  filename: string;
  size: number;
  lastModified: string;
  url: string;
}

export interface UploadResponse {
  uploadUrl: string;
  mediaId: string;
  key: string;
  url: string;
}

// ─── Content Schema ──────────────────────────────────────────────────

export type FieldType = 'text' | 'textarea' | 'richtext' | 'image' | 'images' | 'boolean' | 'select';

export interface FieldSchema {
  type: FieldType;
  label: string;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
  max?: number;
  default?: unknown;
  /** When set to "richtext", the field renders with the WYSIWYG editor + preview */
  editor?: 'richtext';
  /** Human-friendly help text shown below the field */
  hint?: string;
  /** Hide this field from the editor UI (still stored in data) */
  hidden?: boolean;
  /** Options for select fields */
  options?: { value: string; label: string }[];
}

export interface ContentTypeSchema {
  singleton: boolean;
  fields: Record<string, FieldSchema>;
}

export interface TemplateSchema {
  templateId: string;
  templateName: string;
  description: string;
  contentTypes: Record<string, ContentTypeSchema>;
}

// ─── API ─────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
}

export interface ApiError {
  error: string;
}

// ─── Toast ───────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

// ─── Template Manifest ───────────────────────────────────────────────

export interface TemplateManifest {
  id: string;
  name: string;
  description: string;
  version: string;
  tier: 'free' | 'premium';
  features: string[];
  sectionLabels: string[];
  fields: Record<string, string[]>;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
}
